import type { RoleEnum } from '@zeus/index'
import { useMutation } from '@apollo/client/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import { BrandLogo } from '~/components/BrandLogo'
import { MAIN_ID } from '~/components/SkipLink'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { GQL_LOGIN_UNIQUE } from '~/gql/auth/auth'
import { useAuth } from '~/helpers/store/auth'
import { getDefaultRoute } from '~/routes/protected-loader'

/**
 * Atterrissage du retour SSO (#40). L'API a validé l'identité auprès de l'IdP
 * et renvoyé ici avec un jeton de passage à usage unique ; on l'échange
 * immédiatement contre une session.
 */
function SsoCallback() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { setPayload, setLoading } = useAuth()

  const token = searchParams.get('token')
  const [failed, setFailed] = useState(false)
  // Le jeton est à usage unique : ni StrictMode ni un re-render ne doivent
  // déclencher un second échange, qui échouerait.
  const consumed = useRef(false)

  const [uniqueAuthFromSso] = useMutation(GQL_LOGIN_UNIQUE)

  useEffect(() => {
    if (!token) {
      navigate('/login', { replace: true })
      return
    }
    if (consumed.current) return
    consumed.current = true

    const run = async () => {
      try {
        const { data } = await uniqueAuthFromSso({ variables: { token } })
        const user = data?.uniqueAuthFromSso
        if (!user) {
          setFailed(true)
          return
        }

        setPayload({
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          id: user.id,
        })
        setLoading(false)
        toast.success(t('connection.successful'))
        // `replace` : l'URL porte le jeton, elle ne doit pas rester dans
        // l'historique de navigation.
        navigate(getDefaultRoute(user.role as RoleEnum), { replace: true })
      } catch {
        setFailed(true)
      }
    }

    void run()
  }, [token, navigate, uniqueAuthFromSso, setPayload, setLoading, t])

  return (
    <main id={MAIN_ID} className="flex min-h-dvh w-full flex-col items-center justify-center p-5">
      <Card className="sm:w-md w-full">
        <CardHeader className="gap-4">
          <BrandLogo className="h-9 w-auto" fallbackClassName="text-lg" />
          <div className="flex flex-col gap-1">
            <CardTitle as="h1" className="font-heading text-2xl tracking-tight">
              {failed ? t('sso.callback.failed.title') : t('sso.callback.checking')}
            </CardTitle>
            <CardDescription>
              {failed ? t('sso.callback.failed.description') : t('sso.callback.checking.description')}
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          {failed
            ? (
                <Button className="w-full" onClick={() => navigate('/login')}>
                  {t('login.return-message')}
                </Button>
              )
            : (
                <div className="flex justify-center py-2">
                  <Spinner className="size-6 text-muted-foreground" />
                </div>
              )}
        </CardContent>
      </Card>
    </main>
  )
}

export default SsoCallback
